import {
  buildDetachedWindowUrl,
  detachedWindowName,
  generateDetachedWindowId,
  loadDetachedWindowData,
  postTabSyncMessage,
  saveDetachedTabData,
  saveDetachedWindowData,
  type DetachableTabData,
  type DetachedWindowData,
} from "@/lib/detached-tab-sync"

let activeDetachedWindowId: string | null = null
let activeDetachedWindowTarget: string | null = null

export function setActiveDetachedWindow(
  windowId: string | null,
  windowTarget?: string | null,
): void {
  activeDetachedWindowId = windowId
  activeDetachedWindowTarget = windowTarget ?? (windowId ? detachedWindowName(windowId) : null)
}

export function getActiveDetachedWindowId(): string | null {
  return activeDetachedWindowId
}

export function focusActiveDetachedWindow(): void {
  if (!activeDetachedWindowTarget) return
  try {
    const win = window.open("", activeDetachedWindowTarget)
    win?.focus()
  } catch {
    // popup blocked or cross-origin — ignore
  }
}

function defaultWindowSize(tab: DetachableTabData): { width: number; height: number } {
  return tab.type === "editor"
    ? { width: 800, height: 600 }
    : { width: 600, height: 700 }
}

function openDetachedBrowserWindow(
  windowId: string,
  tab: DetachableTabData,
): Window | null {
  const target = detachedWindowName(windowId)
  const { width, height } = defaultWindowSize(tab)
  const left = window.screenX + 100
  const top = window.screenY + 100

  const newWindow = window.open(
    buildDetachedWindowUrl(windowId),
    target,
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
  )

  setActiveDetachedWindow(windowId, target)
  if (newWindow) newWindow.focus()
  return newWindow
}

function addTabToDetachedWindow(
  windowId: string,
  tab: DetachableTabData,
): void {
  const existing = loadDetachedWindowData(windowId)
  if (!existing) return

  if (existing.tabs.some((t) => t.id === tab.id)) {
    focusActiveDetachedWindow()
    return
  }

  const updated: DetachedWindowData = {
    ...existing,
    tabs: [...existing.tabs, tab],
    activeTabId: tab.id,
  }
  saveDetachedWindowData(updated)
  postTabSyncMessage({ type: "TAB_ADD_TO_WINDOW", windowId, tab })
  focusActiveDetachedWindow()
}

/**
 * Open a new detached window or add the tab to the active detached window.
 * Returns the new Window when one was created, otherwise null.
 */
export function openOrAddDetachedTab(tab: DetachableTabData): Window | null {
  saveDetachedTabData(tab)
  postTabSyncMessage({ type: "TAB_DETACHED", tabId: tab.id })

  const existingWindowId = getActiveDetachedWindowId()
  if (existingWindowId && loadDetachedWindowData(existingWindowId)) {
    addTabToDetachedWindow(existingWindowId, tab)
    return null
  }

  const windowId = generateDetachedWindowId()
  const windowData: DetachedWindowData = {
    id: windowId,
    tabs: [tab],
    activeTabId: tab.id,
  }
  saveDetachedWindowData(windowData)
  return openDetachedBrowserWindow(windowId, tab)
}
